// Combobox mit Listbox nach APG (ST-SEA-002 S2a), gemeinsam fuer die
// Befehlspalette und Felder mit Vorschlaegen: der Fokus bleibt im Feld,
// aria-activedescendant zeigt auf die aktive Option.
//
// Enter waehlt nur eine aktive Option; ohne sie geht Enter weiter an das
// Formular, damit "Suchen" die Hauptaktion bleibt.
//
// Tab laesst der Haken in Ruhe, ausser ein Feld schaltet `vormerken` ein
// (Michaels Rundgang 2/19: Vorschlag mit Tab uebernehmen). Dann ist bei
// offener Liste ohne Pfeilwahl die erste Option nur optisch vorgemerkt, ohne
// aria-activedescendant, und Tab ohne Modifier uebernimmt die aktive oder die
// vorgemerkte Option; der Fokus bleibt im Feld. Das Feld schaltet `vormerken`
// nur ein, solange seit der letzten Uebernahme getippt wurde und es nicht leer
// ist. So faengt der Haken hoechstens ein Tab ab, und das ist sichtbar und
// angesagt; Shift+Tab und jedes weitere Tab verlassen das Feld (NFR-ACC-001).

import { useEffect, useState, type KeyboardEvent } from 'react';

/**
 * Stelle der aktiven Option oder -1. Die gemerkte Wahl gilt nur, solange es
 * sie noch gibt; sonst ist mit `ersteIstAktiv` die erste aktiv.
 */
export function aktiveStelle(gewaehlt: number, anzahl: number, ersteIstAktiv: boolean): number {
  if (gewaehlt >= 0 && gewaehlt < anzahl) {
    return gewaehlt;
  }
  return ersteIstAktiv && anzahl > 0 ? 0 : -1;
}

/** Pfeiltasten laufen im Kreis; ohne aktive Option beginnt ↓ oben und ↑ unten. */
export function naechsteStelle(stelle: number, anzahl: number, schritt: 1 | -1): number {
  if (anzahl === 0) {
    return -1;
  }
  if (stelle < 0) {
    return schritt === 1 ? 0 : anzahl - 1;
  }
  return (stelle + schritt + anzahl) % anzahl;
}

export interface ComboboxOptionen<T> {
  readonly optionen: readonly T[];
  /** Stabile Kennung je Option; daraus wird die DOM-id. */
  readonly schluessel: (option: T) => string;
  readonly idPraefix: string;
  readonly listId: string;
  readonly offen: boolean;
  /** Palette: Enter nimmt die erste. Feld mit Vorschlaegen: erst nach ↓. */
  readonly ersteIstAktiv: boolean;
  readonly onWaehlen: (option: T, weg: 'taste' | 'tab' | 'klick') => void;
  /** Schliesst die offene Liste; ohne ihn geht Escape weiter (Dialog). */
  readonly onEscape?: () => void;
  /** Erste Option vormerken und Tab uebernehmen lassen (siehe Kopf). */
  readonly vormerken?: boolean;
}

export interface Combobox<T> {
  readonly aktiv: T | undefined;
  /** Die nur optisch vorgemerkte Option, die Tab uebernimmt; nie zugleich mit `aktiv`. */
  readonly vorgemerkt: T | undefined;
  /** Beim Tippen: die gemerkte Wahl verwerfen. */
  readonly zuruecksetzen: () => void;
  readonly eingabeAttribute: {
    readonly role: 'combobox';
    readonly 'aria-expanded': boolean;
    readonly 'aria-controls': string;
    readonly 'aria-autocomplete': 'list';
    readonly 'aria-activedescendant'?: string;
  };
  readonly beiTaste: (ereignis: KeyboardEvent<HTMLInputElement>) => void;
  readonly optionAttribute: (option: T) => {
    readonly id: string;
    readonly role: 'option';
    readonly 'aria-selected': boolean;
    readonly 'aria-setsize': number;
    readonly 'aria-posinset': number;
    readonly onMouseMove: () => void;
    readonly onMouseDown: (ereignis: { preventDefault: () => void }) => void;
    readonly onClick: () => void;
  };
}

export function useCombobox<T>(optionen: ComboboxOptionen<T>): Combobox<T> {
  const { optionen: liste, schluessel, idPraefix, listId, offen, ersteIstAktiv, onWaehlen, onEscape, vormerken = false } = optionen;
  const [gewaehltId, setGewaehltId] = useState<string | undefined>(undefined);

  const gewaehlt = gewaehltId === undefined ? -1 : liste.findIndex((option) => schluessel(option) === gewaehltId);
  const stelle = offen ? aktiveStelle(gewaehlt, liste.length, ersteIstAktiv) : -1;
  const aktiv = stelle < 0 ? undefined : liste[stelle];
  const aktivDomId = aktiv === undefined ? undefined : `${idPraefix}${schluessel(aktiv)}`;
  const vorgemerkt = vormerken && offen && aktiv === undefined ? liste[0] : undefined;

  useEffect(() => {
    if (aktivDomId !== undefined) {
      document.getElementById(aktivDomId)?.scrollIntoView?.({ block: 'nearest' });
    }
  }, [aktivDomId]);

  const beiTaste = (ereignis: KeyboardEvent<HTMLInputElement>): void => {
    if (!offen) {
      return;
    }
    if (ereignis.key === 'ArrowDown' || ereignis.key === 'ArrowUp') {
      ereignis.preventDefault();
      const neu = liste[naechsteStelle(stelle, liste.length, ereignis.key === 'ArrowDown' ? 1 : -1)];
      setGewaehltId(neu === undefined ? undefined : schluessel(neu));
    } else if (ereignis.key === 'Enter' && aktiv !== undefined) {
      ereignis.preventDefault();
      onWaehlen(aktiv, 'taste');
    } else if (ereignis.key === 'Tab' && vormerken && !ereignis.shiftKey && !ereignis.altKey && !ereignis.ctrlKey && !ereignis.metaKey) {
      const ziel = aktiv ?? vorgemerkt;
      if (ziel !== undefined) {
        ereignis.preventDefault();
        onWaehlen(ziel, 'tab');
      }
    } else if (ereignis.key === 'Escape' && onEscape !== undefined) {
      ereignis.preventDefault();
      onEscape();
    }
  };

  return {
    aktiv,
    vorgemerkt,
    zuruecksetzen: () => setGewaehltId(undefined),
    eingabeAttribute: {
      role: 'combobox',
      'aria-expanded': offen,
      'aria-controls': listId,
      'aria-autocomplete': 'list',
      ...(aktivDomId === undefined ? {} : { 'aria-activedescendant': aktivDomId }),
    },
    beiTaste,
    optionAttribute: (option) => ({
      id: `${idPraefix}${schluessel(option)}`,
      role: 'option',
      'aria-selected': option === aktiv,
      'aria-setsize': liste.length,
      'aria-posinset': liste.indexOf(option) + 1,
      onMouseMove: () => setGewaehltId(schluessel(option)),
      // Der Fokus bleibt im Feld, sonst verlöre die Liste ihren Bezug.
      onMouseDown: (ereignis) => ereignis.preventDefault(),
      onClick: () => onWaehlen(option, 'klick'),
    }),
  };
}
